import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs/operators';
import { Labels, StatesObj } from 'src/app/app.models';
import { MobileBreakPointsService } from 'src/app/services/mobileBreakPoints.service';
import {
  dropdownAnimation,
  slideInOutAnimation,
  overlayFadeInOutAnimation,
  fadeInOutAnimation,
} from 'src/app/shared/reusable-animations/card-estimate-drawer-animations';
import { StatesConfig } from '../card-estimate.component';
import { StringUtils } from 'src/app/shared/utils/string-utils';
import { values } from 'ramda';

@Component({
  selector: 'app-custom-state-input-selector',
  templateUrl: './custom-state-input-selector.component.html',
  styleUrls: ['./custom-state-input-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomStateInputSelectorComponent),
      multi: true,
    },
  ],
  animations: [
    dropdownAnimation,
    slideInOutAnimation,
    overlayFadeInOutAnimation,
    fadeInOutAnimation,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomStateInputSelectorComponent
  implements OnInit, ControlValueAccessor, OnChanges
{
  private _value: string;
  isStateDropdownEnabled: boolean = false;

  _isStateDropdownEnabledSubBehavior$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isStateDropdownEnabled$: Observable<boolean> =
    this._isStateDropdownEnabledSubBehavior$.asObservable();
  isMobile$: Observable<boolean>;
  onChange: Function = (value: string) => {};
  onTouch: Function = () => {};
  stateControl: FormControl<string | null> = new FormControl('', [
    Validators.required,
  ]);

  stateOrProvinceFiltered$: Observable<StatesObj[]>;
  @Input() flow!: string;
  @Input() states: StatesConfig[];
  _labels: Labels;
  @Input() set labels(labels: Labels) {
    this._labels = labels;
  }
  public get labels() {
    return this._labels;
  }

  private _stateCode: string;

  @Input() set stateCode(stateCode: string | undefined) {
    this._stateCode = stateCode!;
  }

  get stateCode() {
    return this._stateCode;
  }

  private _stateOrProvinceSelected: string;

  public get stateOrProvinceSelected(): string {
    return this._stateOrProvinceSelected;
  }
  constructor(private mobileBreakPoints: MobileBreakPointsService) {}

  @ViewChild('containerWrapper', { static: false, read: ElementRef })
  containerWrapper!: ElementRef;
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const isStateDropDownValueBS: boolean =
      this._isStateDropdownEnabledSubBehavior$.getValue();
    if (
      isStateDropDownValueBS &&
      this.containerWrapper &&
      this.isStateDropdownEnabled &&
      !this.containerWrapper.nativeElement.contains(event.target)
    ) {
      this.isStateDropdownEnabled = false;
      this._isStateDropdownEnabledSubBehavior$.next(false);
      this._reseStateControlAndSelectedStateOrProvinceSelected();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['states']?.currentValue) {
      const stateName = this.states.find(
        (state: StatesConfig) => state.code === this.stateCode
      )?.name!;

      if (stateName) {
        this.stateControl.setValue(stateName);
      }
    }
  }

  ngOnInit(): void {
    this.isMobile$ = this.mobileBreakPoints
      .mobileBreakPoints$ as Observable<boolean>;
    this.stateControl?.valueChanges?.subscribe((value) => {
      if (value) {
        this.onChange(value);
        this.onTouch();
      } else {
        this._stateOrProvinceSelected = '';
        this.onChange('');
        this.onTouch();
      }
    });

    this.stateOrProvinceFiltered$ = this.stateControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.statesFilter(value || ''))
    );

    combineLatest([
      this.stateOrProvinceFiltered$,
      this.stateControl.valueChanges,
    ])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([state, value]: [StatesObj[], string | null]) => {
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (state.length === 1) {
          const option = state[0];
          const isMatch =
            filterValue ===
              StringUtils.removeDiacriticsAndConvertToLowercase(option?.name) ||
            filterValue ===
              StringUtils.removeDiacriticsAndConvertToLowercase(option?.code);
          if (isMatch) {
            this.stateControl?.setValue(option?.name, { emitEvent: false });
            this.isStateDropdownEnabled = false;
            this._isStateDropdownEnabledSubBehavior$.next(false);
            this._stateOrProvinceSelected = option.name;
          }
        } else if (state.length > 1) {
          const matchingStates = state.filter(
            (option) =>
              filterValue ===
                StringUtils.removeDiacriticsAndConvertToLowercase(
                  option?.name
                ) ||
              filterValue ===
                StringUtils.removeDiacriticsAndConvertToLowercase(option?.code)
          );
          if (matchingStates.length === 1) {
            const option = matchingStates[0];
            this.stateControl?.setValue(option?.name, { emitEvent: false });
            this.isStateDropdownEnabled = false;
            this._isStateDropdownEnabledSubBehavior$.next(false);
            this._stateOrProvinceSelected = option.name;
          }
        } else if (filterValue && !state.length) {
          this.stateControl?.setErrors({ invalidOption: true });
        } else if (!filterValue && state.length) {
          this.stateControl?.setErrors({ required: true });
        } else {
          this.stateControl?.setErrors({ invalidOption: true });
        }
      });
  }
  private statesFilter(value: string): StatesConfig[] {
    const filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.states.filter((option: StatesObj) => {
      return (
        (option?.name &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.name
          ).startsWith(filterValue)) ||
        (option?.code &&
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.code
          ).startsWith(filterValue))
      );
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    if (obj !== undefined && obj !== null) {
      this._value = obj;
      this.stateControl.setValue(this.displayStateOrProvinceName(this._value), {
        emitEvent: false,
      });
      this._stateOrProvinceSelected = this.stateControl.value!;
      this.onTouch();
    }
  }

  selectStateOrProvince(value: string) {
    this.stateControl.setValue(this.displayStateOrProvinceName(value), {
      emitEvent: true,
    });
    this._stateOrProvinceSelected = this.stateControl.value!;
    this.isStateDropdownEnabled = false;
    this._isStateDropdownEnabledSubBehavior$.next(false);
  }

  displayStateOrProvinceName(value: string): string {
    const stateProvinceName = this.states.find(
      (state: StatesObj) => state.code === value
    )?.name!;
    return stateProvinceName;
  }

  removeValue() {
    this.stateControl.setValue('', { emitEvent: true });
    this._stateOrProvinceSelected = '';
  }

  toggleStateDropdown() {
    this.isStateDropdownEnabled = !this.isStateDropdownEnabled;
    const isStateDropDownValueBS: boolean =
      this._isStateDropdownEnabledSubBehavior$.getValue();
    this._isStateDropdownEnabledSubBehavior$.next(!isStateDropDownValueBS);
    this._reseStateControlAndSelectedStateOrProvinceSelected();
  }

  private _reseStateControlAndSelectedStateOrProvinceSelected() {
    const stateOrProvinceControl = this.stateControl;
    if (!stateOrProvinceControl) return;
    const stateOrProvinceValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(
        stateOrProvinceControl?.value ?? ''
      );
    const foundStateOrProvince = this.states.some(
      (stateOrProvince) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(
          stateOrProvince?.name
        ) === stateOrProvinceValue ||
        StringUtils.removeDiacriticsAndConvertToLowercase(
          stateOrProvince?.code
        ) === stateOrProvinceValue
    );
    if (!foundStateOrProvince || !stateOrProvinceValue) {
      stateOrProvinceControl.setValue('');
      this._stateOrProvinceSelected = '';
    }
  }
}
